// src/components/admin/EnforcedLayoutCard.tsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { LayoutSettingsPanel } from '@/components/layouts/LayoutSettingsPanel';
import CampaignCompositePreview, {
  type CompositeWatermark,
  type CompositeSubtitlePosition,
} from '@/components/admin/CampaignCompositePreview';
import { buildDefaults } from '@/lib/layouts/buildDefaults';
import { resolveFields } from '@/lib/layouts/resolveFields';
import { POST_LAYOUTS, POST_LAYOUT_ADMIN_ORDER } from '@/lib/layouts/registry';
import type { EnforcedPostLayout } from '@/lib/layouts/applyEnforcedLayout';
import type { CirculoConfig, CirculoPresetId, PostLayoutConfig, PostLayoutFieldValue } from '@/lib/layouts/types';
import { applyPostLayoutField } from '@/lib/layouts/applyPostLayoutField';
import { CIRCULO_PRESET_IDS, circuloConfigForPreset, resolveCirculoPreset } from '@/lib/layouts/resolveCirculoPreset';

function buildInitialBlob(): EnforcedPostLayout {
  const defaults = buildDefaults('satisfatorio', {});
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { kind: _kind, ...config } = defaults as unknown as { kind: string } & Record<string, unknown>;
  return { kind: 'satisfatorio', config, locked_fields: [] };
}

function seedConfig(kind: string): EnforcedPostLayout {
  const defaults = buildDefaults(kind as Parameters<typeof buildDefaults>[0], {});
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { kind: _kind, ...config } = defaults as unknown as { kind: string } & Record<string, unknown>;
  return { kind, config, locked_fields: [] };
}

export interface EnforcedLayoutCardProps {
  value: EnforcedPostLayout | null;
  onChange: (v: EnforcedPostLayout | null) => void;
  /** Other campaign enforcements, composed into the preview for consistency. */
  watermark?: CompositeWatermark | null;
  subtitlePosition?: CompositeSubtitlePosition | null;
}


const EnforcedLayoutCard: React.FC<EnforcedLayoutCardProps> = ({
  value,
  onChange,
  watermark,
  subtitlePosition,
}) => {
  const { t } = useTranslation();

  const enabled = value !== null;

  const handleToggle = () => {
    if (enabled) {
      onChange(null);
    } else {
      onChange(buildInitialBlob());
    }
  };

  const handleKindChange = (kind: string) => {
    onChange(seedConfig(kind));
  };

  const handleCirculoPresetChange = (preset: CirculoPresetId) => {
    if (!value) return;
    if (value.kind === 'circulo') {
      const previous = { kind: 'circulo' as const, ...value.config } as CirculoConfig;
      const next = circuloConfigForPreset(preset, previous);
      const { kind, ...config } = next;
      onChange({ ...value, kind, config });
      return;
    }
    const next = circuloConfigForPreset(preset);
    const { kind, ...config } = next;
    onChange({ kind, config, locked_fields: [] });
  };

  const flatConfig: PostLayoutConfig = value
    ? ({ kind: value.kind, ...value.config } as PostLayoutConfig)
    : ({ kind: 'satisfatorio' } as PostLayoutConfig);

  const fields = value
    ? resolveFields(POST_LAYOUTS[value.kind as keyof typeof POST_LAYOUTS], flatConfig)
    : [];

  const handleFieldChange = (key: string, val: PostLayoutFieldValue) => {
    if (!value) return;
    const nextFlat = applyPostLayoutField(flatConfig, key, val, false);
    const { kind, ...config } = nextFlat;
    onChange({ ...value, kind, config });
  };

  const handleLockToggle = (key: string) => {
    if (!value) return;
    const locked = value.locked_fields ?? [];
    const next = locked.includes(key)
      ? locked.filter((k) => k !== key)
      : [...locked, key];
    onChange({ ...value, locked_fields: next });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('admin.campaigns.edit.enforcedLayout.cardTitle')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          {t('admin.campaigns.edit.enforcedLayout.description')}
        </p>

        <div className="flex items-center gap-3">
          <Switch
            checked={enabled}
            onCheckedChange={handleToggle}
            data-testid="enforced-layout-enable"
            id="enforced-layout-switch"
          />
          <Label htmlFor="enforced-layout-switch">
            {t('admin.campaigns.edit.enforcedLayout.enable')}
          </Label>
        </div>

        {enabled && value && (
          <div className="space-y-4">
            {/* Kind picker */}
            <div className="flex flex-wrap gap-2">
              {POST_LAYOUT_ADMIN_ORDER.filter((k) => k !== 'circulo').map((kind) => (
                <button
                  key={kind}
                  type="button"
                  data-testid={`kind-btn-${kind}`}
                  onClick={() => handleKindChange(kind)}
                  className={`text-xs px-3 py-1.5 rounded-md border transition-colors ${
                    value.kind === kind
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border bg-muted/30 hover:bg-muted/50'
                  }`}
                >
                  {t(POST_LAYOUTS[kind as keyof typeof POST_LAYOUTS].labelKey)}
                </button>
              ))}
              {CIRCULO_PRESET_IDS.map((preset) => {
                const selected = value.kind === 'circulo'
                  && resolveCirculoPreset({
                    preset: typeof value.config.preset === 'string' ? value.config.preset : null,
                  }) === preset;
                return (
                  <button
                    key={preset}
                    type="button"
                    data-testid={`kind-btn-circulo-${preset}`}
                    onClick={() => handleCirculoPresetChange(preset)}
                    className={`text-xs px-3 py-1.5 rounded-md border transition-colors ${
                      selected
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-border bg-muted/30 hover:bg-muted/50'
                    }`}
                  >
                    {t(`projectClips.postLayouts.circulo.${preset}`)}
                  </button>
                );
              })}
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
            <CampaignCompositePreview
              testId="enforced-layout-preview"
              postLayout={flatConfig}
              watermark={watermark}
              subtitlePosition={subtitlePosition}
              width={200}
            />

            {/* Per-field editor with lock toggles */}
            <div className="space-y-3 flex-1">
              {fields.map((f) => {
                const isLocked = (value.locked_fields ?? []).includes(f.key);
                return (
                  <div key={f.key} className="space-y-1">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs text-muted-foreground">{t(f.labelKey)}</span>
                      <button
                        type="button"
                        data-testid={`lock-${f.key}`}
                        onClick={() => handleLockToggle(f.key)}
                        className={`text-xs px-2 py-0.5 rounded border transition-colors ${
                          isLocked
                            ? 'border-primary bg-primary/10 text-primary'
                            : 'border-border bg-muted/30 hover:bg-muted/50'
                        }`}
                      >
                        {isLocked
                          ? t('admin.campaigns.edit.enforcedLayout.locked')
                          : t('admin.campaigns.edit.enforcedLayout.default')}
                      </button>
                    </div>
                    <LayoutSettingsPanel
                      fields={[f]}
                      config={flatConfig}
                      onChange={handleFieldChange}
                    />
                  </div>
                );
              })}
            </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default EnforcedLayoutCard;
